import React, { useCallback, useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import {
  Button,
  IconButton,
  IconButtonOwnProps,
  InputAdornment,
  InputBase,
  MenuItem,
  Popover,
  Stack
} from "@mui/material";
import { useActionHook } from "hooks";
import { debounce } from "lodash";
import { useDealActions } from "store";

interface Props {
  iconButtonStyles?: IconButtonOwnProps["sx"];
  selectedKeys: string[];
  onSubmit: (keys: string[]) => void;
}

export const ColumnsSetMenu: React.FC<Props> = ({ iconButtonStyles, selectedKeys, onSubmit }) => {
  const { getDealsTableColumnsActions } = useDealActions();

  const { action, data: allOptions } = useActionHook(getDealsTableColumnsActions, {
    onSuccess: (res) => setDisplayedOptions(res)
  });
  const [displayedOptions, setDisplayedOptions] = useState<
    {
      key: string;
      title: string;
    }[]
  >([]);

  useEffect(() => {
    action();
  }, []);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [localState, setLocalState] = useState<string[]>(selectedKeys);

  const handleOpen = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setLocalState(selectedKeys);
      setAnchorEl(e.currentTarget);
    },
    [selectedKeys]
  );

  const handleClose = useCallback(() => {
    setLocalState(selectedKeys);
    setAnchorEl(null);
    setDisplayedOptions(allOptions || []);
  }, [selectedKeys, allOptions]);

  const handleItemClick = useCallback(
    (key: string) => {
      setLocalState((prevState) => {
        if (prevState.includes(key)) {
          return prevState.filter((item) => item !== key);
        } else {
          return [...prevState, key];
        }
      });
    },
    [setLocalState]
  );

  const handleSubmit = useCallback(() => {
    onSubmit(localState);
    handleClose();
  }, [onSubmit, localState, handleClose]);

  const search = useCallback(
    debounce((searchValue: string) => {
      searchValue = searchValue.trim();
      if (!allOptions) return [];
      if (!searchValue) {
        return setDisplayedOptions(allOptions);
      }

      setDisplayedOptions(
        allOptions.filter((item) => item.title.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()))
      );
    }, 300),
    [allOptions]
  );

  if (!allOptions) return;

  return (
    <>
      <IconButton
        sx={{ bgcolor: "#0A3142", ":hover": { bgcolor: "#0A3142" }, ...iconButtonStyles }}
        size="sm"
        onClick={handleOpen}
      >
        <AddIcon sx={{ color: "#fff" }} />
      </IconButton>
      <Popover
        onClose={handleClose}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
      >
        <Stack sx={{ width: 240, overflow: "hidden", boxShadow: "sm", borderRadius: "8px" }}>
          <Stack sx={{ p: "2px 5px" }}>
            <InputBase
              autoFocus
              onChange={(e) => search(e.target.value)}
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search characteristic"
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#757575" }} />
                </InputAdornment>
              }
            />
          </Stack>
          <Stack sx={{ maxHeight: 360, overflow: "auto", pb: "4px" }}>
            {displayedOptions.map((item) => (
              <MenuItem
                onClick={() => handleItemClick(item.key)}
                sx={{
                  ":hover": {
                    bgcolor: "#dfe6e9"
                  },
                  ...(localState.includes(item.key) && { bgcolor: "rgb(219, 232, 245)" })
                }}
                key={item.key}
              >
                {item.title}
              </MenuItem>
            ))}
          </Stack>
          <Stack
            sx={{
              flexDirection: "row",
              justifyContent: "flex-end",
              gap: "5px",
              p: "8px 16px",
              boxShadow: "0px 1px 1px #888, 0px -1px 5px #888"
            }}
          >
            <Button onClick={handleClose} size="sm" variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleSubmit} size="sm" variant="contained">
              Apply
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};
